import gql from 'graphql-tag';

import { mapPersonaForBackendInput } from 'src/data/persona';
import { executeGqlMutation } from 'src/graphql/client';
import { FRAGMENT_JOB_ID, waitForJobWithEntityResult } from 'src/graphql/job';
import { stringifyIfPresent } from 'src/lib/common';
import { getGqlFragmentName, getSingleProp } from 'src/lib/graphql';

import { FRAGMENT_SUGGEST_REQUEST } from './schema';

/**
 * "suggest" generation for short-form
 * @param {Object} params
 * @param {Object} [options]
 * @returns {Promise<Object[]>}
 */
export const suggestDocumentVariations = async (
  {
    projectId,
    generationTool,
    subGenerationToolsProps,
    brief,
    inputText,
    scoringMethod,
    suggestionMode,
    language,
    numberOfVariations,
    tovInput,
    debugAcPrompt,
    freestyleToolId,
    freestyleToolAllInstructions,
    freestyleToolAllInstructionsData,
    languageTargetFormality,
    customerPersona,
    annotations,
    sourceType,
    url,
    manualInstructions,
    avoidWords,
    onlyImproveScore,
    originalVariationId,
    extractedBrief,
    urlExtractions,
    callToAction,
    generationCustomModelId,
    imageAspectRatio,
    imageModel,
    imageStyleId,
    imageOverlayProperties,
    availableTools,
    structureInstructionsData,
    mediaGenerationProperties,
    subTemplateProperties,
  },
  options = null
) => {
  const resultFragment = FRAGMENT_SUGGEST_REQUEST;
  const fragment = options?.async ? FRAGMENT_JOB_ID : resultFragment;
  const mutation = gql`
    mutation SuggestDocumentVariations(
      $async: Boolean
      $projectId: ID!
      $generationTool: GenerationToolEnum!
      $subGenerationToolsProps: JSON
      $brief: BriefInput!
      $inputText: String
      $scoringMethod: ScoringMethodInput
      $suggestionMode: String!
      $language: String!
      $languageTargetFormality: LanguageTargetFormalityEnum
      $numberOfVariations: Int!
      $tovInput: TovInput
      $debugAcPrompt: String
      $freestyleToolId: ID
      $freestyleToolAllInstructions: String
      $freestyleToolAllInstructionsData: JSON
      $customerPersonaId: ID
      $customerPersona: CustomerPersonaInput
      $annotations: [SuggestionAnnotationInput!]
      $sourceType: String
      $url: String
      $manualInstructions: String
      $avoidWords: [String!]
      $onlyImproveScore: Boolean
      $originalVariationId: ID
      $extractedBrief: String
      $urlExtractions: [UrlExtractionInput!]
      $callToAction: String
      $generationCustomModelId: ID
      $imageAspectRatio: String
      $imageModel: String
      $imageStyleId: ID
      $imageOverlayProperties: ImageOverlayTextInput
      $availableTools: JSON
      $structureInstructionsData: JSON
      $mediaGenerationProperties: MediaGenerationInput
      $subTemplateProperties: SubTemplateInput
    ) {
      suggestVariationsForDocumentEditor(
        async: $async
        projectId: $projectId
        generationTool: $generationTool
        subGenerationToolsProps: $subGenerationToolsProps
        brief: $brief
        inputText: $inputText
        scoringMethod: $scoringMethod
        suggestionMode: $suggestionMode
        language: $language
        languageTargetFormality: $languageTargetFormality
        numberOfVariations: $numberOfVariations
        tovInput: $tovInput
        debugAcPrompt: $debugAcPrompt
        freestyleToolId: $freestyleToolId
        freestyleToolAllInstructions: $freestyleToolAllInstructions
        freestyleToolAllInstructionsData: $freestyleToolAllInstructionsData
        customerPersonaId: $customerPersonaId
        customerPersona: $customerPersona
        annotations: $annotations
        sourceType: $sourceType
        url: $url
        manualInstructions: $manualInstructions
        avoidWords: $avoidWords
        onlyImproveScore: $onlyImproveScore
        originalVariationId: $originalVariationId
        extractedBrief: $extractedBrief
        urlExtractions: $urlExtractions
        callToAction: $callToAction
        generationCustomModelId: $generationCustomModelId
        imageAspectRatio: $imageAspectRatio
        imageModel: $imageModel
        imageStyleId: $imageStyleId
        imageOverlayProperties: $imageOverlayProperties
        availableTools: $availableTools
        structureInstructionsData: $structureInstructionsData
        mediaGenerationProperties: $mediaGenerationProperties
        subTemplateProperties: $subTemplateProperties
      ) {
        ...${getGqlFragmentName(fragment)}
      }
    }
    ${fragment}
  `;

  const variables = {
    async: options?.async ?? false,
    projectId,
    generationTool,
    subGenerationToolsProps: stringifyIfPresent(subGenerationToolsProps),
    brief,
    inputText,
    scoringMethod,
    suggestionMode,
    language,
    numberOfVariations,
    tovInput,
    debugAcPrompt,
    freestyleToolId,
    freestyleToolAllInstructions,
    freestyleToolAllInstructionsData: stringifyIfPresent(freestyleToolAllInstructionsData),
    languageTargetFormality,
    customerPersonaId: customerPersona?.id,
    customerPersona: mapPersonaForBackendInput(customerPersona),
    annotations,
    sourceType,
    url,
    manualInstructions,
    avoidWords,
    onlyImproveScore,
    originalVariationId,
    extractedBrief,
    urlExtractions,
    callToAction,
    generationCustomModelId,
    imageAspectRatio,
    imageModel,
    imageStyleId,
    imageOverlayProperties,
    availableTools,
    structureInstructionsData: stringifyIfPresent(structureInstructionsData),
    mediaGenerationProperties,
    subTemplateProperties,
  };

  const response = await executeGqlMutation({
    mutation,
    variables,
    options: {
      abortSignal: options?.abortSignal,
      traceAcRequests: true,
    },
  });

  const result = getSingleProp(response.data);
  if (!options?.async) {
    // support old BE, which returns a single object instead of an array
    return Array.isArray(result) ? result : [result];
  }

  const job = Array.isArray(result) ? result[0] : result;
  return waitForJobWithEntityResult(job.id, resultFragment, {
    abortSignal: options?.abortSignal,
    onProgress: options?.onJobProgress,
    list: true,
  });
};
